import { BzbsService } from '@bzbs/react-api-client';
import { create } from 'zustand';

export type AuthTokenType = 'jwt' | 'auth_token';

type getTokenFunction = () => Promise<string | null>;
type setTokenFunction = (token: string) => void;
type removeTokenFunction = () => void;

export type TokenFunctions = {
  getToken: getTokenFunction;
  setToken: setTokenFunction;
  removeToken: removeTokenFunction;
};

export type BuzzebeesAppState = {
  appId: string;
  appName: string;
  bzbsService: BzbsService | null;
  uuid: string;
  macAddress: string;
  clientVersion: string;
  os: string;
  platform: string;
  fcmToken: string;
  deviceNotificationEnabled: boolean;
  urls: {
    webCallback: string;
    cart: string;
  };
  tokenFunctions: TokenFunctions | null;
  tokenType: AuthTokenType;
  config: {
    defaultDashboardConfig: string;
    defaultDashboardMode: 'main' | 'sub';
    defaultMenuConfig: string;
    defaultCampaignConfig: string;
  };
};

export type BuzzebeesAppActions = {
  setAppId: (appId: string) => void;
  setAppName: (appName: string) => void;
  setBzbsService: (service: BzbsService) => void;
  setUuid: (uuid: string) => void;
  setMacAddress: (macAddress: string) => void;
  setClientVersion: (clientVersion: string) => void;
  setOs: (os: string) => void;
  setPlatform: (platform: string) => void;
  setFcmToken: (fcmToken: string) => void;
  setDeviceNotificationEnabled: (deviceNotificationEnabled: boolean) => void;
  setUrls: (urls: { webCallback: string; cart: string }) => void;
  setTokenType: (tokenType: AuthTokenType) => void;
  setTokenFunctions: (tokenFunctions: TokenFunctions) => void;
  configure: (config: {
    appId: string;
    appName: string;
    bzbsService: BzbsService;
    uuid: string;
    clientVersion: string;
    os: string;
    platform: string;
    fcmToken: string;
    deviceNotificationEnabled: boolean;
    urls: { webCallback: string; cart: string };
    tokenFunctions: TokenFunctions;
    tokenType: AuthTokenType;
    defaultDashboardConfig: string;
    defaultDashboardMode: 'main' | 'sub';
    defaultMenuConfig: string;
    defaultCampaignConfig: string;
  }) => void;
};

export type BuzzebeesAppStore = BuzzebeesAppState & BuzzebeesAppActions;

export const useBuzzebeesAppStore = create<BuzzebeesAppStore>((set) => ({
  appId: '',
  appName: '',
  bzbsService: null,
  uuid: '',
  macAddress: '',
  clientVersion: '',
  os: '',
  platform: '',
  fcmToken: '',
  deviceNotificationEnabled: false,
  urls: {
    webCallback: '',
    cart: '',
  },
  tokenFunctions: null,
  tokenType: 'auth_token',
  config: {
    defaultDashboardConfig: '',
    defaultDashboardMode: 'main',
    defaultMenuConfig: '',
    defaultCampaignConfig: '',
  },
  setAppId: (appId: string) => set({ appId }),
  setAppName: (appName: string) => set({ appName }),
  setBzbsService: (service: BzbsService) => set({ bzbsService: service }),
  setUuid: (uuid: string) => set({ uuid }),
  setMacAddress: (macAddress: string) => set({ macAddress }),
  setClientVersion: (clientVersion: string) => set({ clientVersion }),
  setOs: (os: string) => set({ os }),
  setPlatform: (platform: string) => set({ platform }),
  setFcmToken: (fcmToken: string) => set({ fcmToken }),
  setDeviceNotificationEnabled: (deviceNotificationEnabled: boolean) => set({ deviceNotificationEnabled }),
  setUrls: (urls: { webCallback: string; cart: string }) => set({ urls }),
  setTokenFunctions: (tokenFunctions: TokenFunctions) => set({ tokenFunctions }),
  setTokenType: (tokenType: AuthTokenType) => set({ tokenType }),
  configure: (config: {
    appId: string;
    appName: string;
    bzbsService: BzbsService;
    uuid: string;
    clientVersion: string;
    os: string;
    platform: string;
    fcmToken: string;
    deviceNotificationEnabled: boolean;
    urls: { webCallback: string; cart: string };
    tokenFunctions: TokenFunctions;
    tokenType: AuthTokenType;
    defaultDashboardConfig: string;
    defaultDashboardMode: 'main' | 'sub';
    defaultMenuConfig: string;
    defaultCampaignConfig: string;
  }) => {
    set({
      appId: config.appId,
      appName: config.appName,
      bzbsService: config.bzbsService,
      uuid: config.uuid,
      clientVersion: config.clientVersion,
      os: config.os,
      platform: config.platform,
      fcmToken: config.fcmToken,
      deviceNotificationEnabled: config.deviceNotificationEnabled,
      urls: config.urls,
      tokenFunctions: config.tokenFunctions,
      tokenType: config.tokenType,
      config: {
        defaultDashboardConfig: config.defaultDashboardConfig,
        defaultDashboardMode: config.defaultDashboardMode,
        defaultMenuConfig: config.defaultMenuConfig,
        defaultCampaignConfig: config.defaultCampaignConfig,
      },
    });
  },
}));
